import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import RefreshIcon from "@material-ui/icons/Refresh";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { getBackendUrl } from "../../config";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  urlRow: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "center",
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
  },
  urlField: {
    flex: 1,
    minWidth: 280,
  },
  secretRow: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "flex-start",
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
  },
  secretField: {
    flex: 1,
    minWidth: 280,
  },
  instructions: {
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary,
    whiteSpace: "pre-line",
  },
  chipOk: {
    backgroundColor: theme.palette.success.light,
  },
  chipWarn: {
    backgroundColor: theme.palette.warning.light,
  },
  mono: {
    fontFamily: "monospace",
    fontSize: "0.8rem",
    maxWidth: 280,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  sectionTitle: {
    marginTop: theme.spacing(1),
  },
  endpointBlock: {
    marginBottom: theme.spacing(2),
  },
  divider: {
    margin: theme.spacing(2, 0),
  },
}));

const fallbackWebhookUrl = (slot = 1) => {
  const base = (getBackendUrl() || "").replace(/\/$/, "");
  const path = slot === 2 ? "/webhooks/stripe-2" : "/webhooks/stripe";
  return base ? `${base}${path}` : path;
};

const statusColor = (status) => {
  if (status === "paid") return "primary";
  if (status === "failed") return "secondary";
  return "default";
};

const secretChipLabel = (configured, last4) => {
  if (!configured) return i18n.t("webhookPage.secretMissing");
  if (last4) return i18n.t("webhookPage.secretOkMasked", { last4 });
  return i18n.t("webhookPage.secretOk");
};

const Webhook = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const isAdmin = user?.profile === "admin";

  const [loading, setLoading] = useState(true);
  const [savingSecret, setSavingSecret] = useState(false);
  const [savingSecret2, setSavingSecret2] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState(fallbackWebhookUrl(1));
  const [webhookUrl2, setWebhookUrl2] = useState(fallbackWebhookUrl(2));
  const [secretConfigured, setSecretConfigured] = useState(false);
  const [secretLast4, setSecretLast4] = useState(null);
  const [secretConfigured2, setSecretConfigured2] = useState(false);
  const [secretLast42, setSecretLast42] = useState(null);
  const [secretInput, setSecretInput] = useState("");
  const [secretInput2, setSecretInput2] = useState("");
  const [logs, setLogs] = useState([]);
  const [events, setEvents] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/stripe-webhook/logs", {
        params: { limit: 50 },
      });
      setWebhookUrl(data.webhookUrl || fallbackWebhookUrl(1));
      setWebhookUrl2(data.webhookUrl2 || fallbackWebhookUrl(2));
      setSecretConfigured(Boolean(data.secretConfigured));
      setSecretLast4(data.secretLast4 || null);
      setSecretConfigured2(Boolean(data.secretConfigured2));
      setSecretLast42(data.secretLast42 || null);
      setLogs(data.logs || []);

      try {
        const info = await api.get("/stripe-webhook/info");
        setEvents(info.data.events || []);
        if (info.data.webhookUrl) setWebhookUrl(info.data.webhookUrl);
        if (info.data.webhookUrl2) setWebhookUrl2(info.data.webhookUrl2);
        if (info.data.secretLast4) setSecretLast4(info.data.secretLast4);
        if (typeof info.data.secretConfigured === "boolean") {
          setSecretConfigured(info.data.secretConfigured);
        }
        if (info.data.secretLast42) setSecretLast42(info.data.secretLast42);
        if (typeof info.data.secretConfigured2 === "boolean") {
          setSecretConfigured2(info.data.secretConfigured2);
        }
      } catch (_) {
        // info is optional
      }

      if (isAdmin) {
        try {
          const secretRes = await api.get("/stripe-webhook/secret");
          setSecretConfigured(Boolean(secretRes.data.configured));
          setSecretLast4(secretRes.data.last4 || null);
        } catch (_) {
          // secret endpoint admin-only; ignore if denied
        }
        try {
          const secretRes2 = await api.get("/stripe-webhook/secret2");
          setSecretConfigured2(Boolean(secretRes2.data.configured));
          setSecretLast42(secretRes2.data.last4 || null);
        } catch (_) {
          // optional until backend deploy
        }
      }
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCopy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(i18n.t("webhookPage.copied"));
    } catch (_) {
      toast.error(i18n.t("webhookPage.copyFail"));
    }
  };

  const saveSecret = async (slot) => {
    if (!isAdmin) return;
    const value = (slot === 2 ? secretInput2 : secretInput).trim();
    if (!value) {
      toast.error(i18n.t("webhookPage.secretRequired"));
      return;
    }
    if (!value.startsWith("whsec_")) {
      toast.error(i18n.t("webhookPage.secretInvalid"));
      return;
    }

    const setSaving = slot === 2 ? setSavingSecret2 : setSavingSecret;
    setSaving(true);
    try {
      const path =
        slot === 2 ? "/stripe-webhook/secret2" : "/stripe-webhook/secret";
      const { data } = await api.put(path, { secret: value });
      if (slot === 2) {
        setSecretConfigured2(Boolean(data.configured));
        setSecretLast42(data.last4 || null);
        setSecretInput2("");
      } else {
        setSecretConfigured(Boolean(data.configured));
        setSecretLast4(data.last4 || null);
        setSecretInput("");
      }
      toast.success(i18n.t("webhookPage.secretSaved"));
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const renderEndpoint = ({
    title,
    url,
    configured,
    last4,
    input,
    setInput,
    saving,
    onSave,
  }) => (
    <div className={classes.endpointBlock}>
      <Typography variant="subtitle1" gutterBottom className={classes.sectionTitle}>
        {title}
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        {i18n.t("webhookPage.urlLabel")}
      </Typography>
      <div className={classes.urlRow}>
        <TextField
          className={classes.urlField}
          variant="outlined"
          size="small"
          value={url}
          InputProps={{ readOnly: true }}
        />
        <Button
          variant="contained"
          color="primary"
          startIcon={<FileCopyOutlinedIcon />}
          onClick={() => handleCopy(url)}
        >
          {i18n.t("webhookPage.copy")}
        </Button>
        <Chip
          size="small"
          label={secretChipLabel(configured, last4)}
          className={configured ? classes.chipOk : classes.chipWarn}
        />
      </div>

      {isAdmin && (
        <>
          <Typography variant="subtitle2" gutterBottom>
            {i18n.t("webhookPage.secretLabel")}
          </Typography>
          <div className={classes.secretRow}>
            <TextField
              className={classes.secretField}
              variant="outlined"
              size="small"
              type="password"
              autoComplete="new-password"
              placeholder={i18n.t("webhookPage.secretPlaceholder")}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              helperText={
                configured
                  ? i18n.t("webhookPage.secretConfiguredHint", {
                      last4: last4 || "****",
                    })
                  : i18n.t("webhookPage.secretHelp")
              }
            />
            <Button
              variant="contained"
              color="primary"
              onClick={onSave}
              disabled={saving || !input.trim()}
            >
              {saving ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                i18n.t("webhookPage.secretSave")
              )}
            </Button>
          </div>
        </>
      )}
    </div>
  );

  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t("webhookPage.title")}</Title>
        <MainHeaderButtonsWrapper>
          <IconButton onClick={load} title={i18n.t("webhookPage.refresh")}>
            <RefreshIcon />
          </IconButton>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        {renderEndpoint({
          title: i18n.t("webhookPage.endpoint1Title"),
          url: webhookUrl,
          configured: secretConfigured,
          last4: secretLast4,
          input: secretInput,
          setInput: setSecretInput,
          saving: savingSecret,
          onSave: () => saveSecret(1),
        })}

        <Divider className={classes.divider} />

        {renderEndpoint({
          title: i18n.t("webhookPage.endpoint2Title"),
          url: webhookUrl2,
          configured: secretConfigured2,
          last4: secretLast42,
          input: secretInput2,
          setInput: setSecretInput2,
          saving: savingSecret2,
          onSave: () => saveSecret(2),
        })}

        <Typography className={classes.instructions} variant="body2">
          {i18n.t("webhookPage.instructions")}
          {events.length > 0 ? `\n\n${events.join("\n")}` : ""}
        </Typography>

        <Typography variant="subtitle1" gutterBottom>
          {i18n.t("webhookPage.logsTitle")}
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{i18n.t("webhookPage.table.createdAt")}</TableCell>
              <TableCell>{i18n.t("webhookPage.table.eventType")}</TableCell>
              <TableCell>{i18n.t("webhookPage.table.status")}</TableCell>
              <TableCell>{i18n.t("webhookPage.table.contact")}</TableCell>
              <TableCell>{i18n.t("webhookPage.table.ticket")}</TableCell>
              <TableCell>{i18n.t("webhookPage.table.pixel")}</TableCell>
              <TableCell>{i18n.t("webhookPage.table.error")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRowSkeleton columns={7} />
            ) : logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography variant="body2" color="textSecondary">
                    {i18n.t("webhookPage.empty")}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              logs.map((row) => {
                let pixelShort = "-";
                try {
                  const pr = row.pixelResult
                    ? JSON.parse(row.pixelResult)
                    : null;
                  if (pr) {
                    pixelShort = pr.action
                      ? `${pr.action}${pr.ok === false ? " ✗" : pr.ok ? " ✓" : ""}`
                      : pr.skipped
                      ? `skip:${pr.reason}`
                      : JSON.stringify(pr).slice(0, 60);
                  }
                } catch (_) {
                  pixelShort = String(row.pixelResult || "-").slice(0, 60);
                }

                return (
                  <TableRow key={row.id}>
                    <TableCell>
                      {row.createdAt
                        ? new Date(row.createdAt).toLocaleString()
                        : "-"}
                    </TableCell>
                    <TableCell className={classes.mono} title={row.eventType}>
                      {row.eventType}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={statusColor(row.status)}
                        label={row.status}
                      />
                    </TableCell>
                    <TableCell>{row.contactId || "-"}</TableCell>
                    <TableCell>{row.ticketId || "-"}</TableCell>
                    <TableCell className={classes.mono} title={pixelShort}>
                      {pixelShort}
                    </TableCell>
                    <TableCell className={classes.mono} title={row.error || ""}>
                      {row.error || "-"}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default Webhook;
